import {
	CalculateSimulationRequest,
	GetSimulationDetailResponse,
} from '@/libs/fleet-api/services/simulation';
import dayjs from 'dayjs';

// Robust mapping from simulationDetailData to form values
export const mapSimulationDetailToFormValues = (
	data: GetSimulationDetailResponse | undefined,
): Partial<CalculateSimulationRequest> => {
	if (!data) return {};
	const result: Partial<CalculateSimulationRequest> = {};

	// calculationTypeId
	if (data.calculationType?.id)
		result.calculationTypeId = Number(data.calculationType?.id);
	// isSplitPo
	if (data?.isSplitPo !== undefined) result.isSplitPo = data?.isSplitPo;
	// dealerSupplier
	if (data?.dealerSupplier) result.dealerSupplier = data?.dealerSupplier;
	// dealerSupplierId
	if (data?.dealerSupplier?.id)
		result.dealerSupplierId = data?.dealerSupplier?.id;
	// dealerSupplierAddress
	if (data?.dealerSupplier?.address)
		result.dealerSupplierAddress = data?.dealerSupplier?.address;
	// dealerSupplierPicName
	if (data?.dealerSupplierPicName)
		result.dealerSupplierPicName = data?.dealerSupplierPicName;
	// dealerSupplierPicPhoneNo
	if (data?.dealerSupplierPicPhoneNo)
		result.dealerSupplierPicPhoneNo = data?.dealerSupplierPicPhoneNo;
	// dealerSupplierPhoneNo
	if (data?.dealerSupplier?.phoneNo)
		result.dealerSupplierPhoneNo = data?.dealerSupplier?.phoneNo;
	// dealerSupplierName
	if (data?.dealerSupplier?.name)
		result.dealerSupplierName = data?.dealerSupplier?.name;
	// karoseriCompanyId
	if (data?.karoseriCompany?.id)
		result.karoseriCompanyId = data?.karoseriCompany?.id;
	// karoseriAddress
	if (data?.karoseriCompany?.address)
		result.karoseriCompanyAddress = data?.karoseriCompany?.address;
	// karoseriCompanyPicName
	if (data?.karoseriCompanyPicName)
		result.karoseriCompanyPicName = data?.karoseriCompanyPicName;
	// karoseriCompanyPicPhoneNo
	if (data?.karoseriCompanyPicPhoneNo)
		result.karoseriCompanyPicPhoneNo = data?.karoseriCompanyPicPhoneNo;
	// karoseriCompanyPhoneNo
	if (data?.karoseriCompany?.phoneNo)
		result.karoseriCompanyPhoneNo = data?.karoseriCompany?.phoneNo;
	// karoseriCompanyName
	if (data?.karoseriCompany?.name)
		result.karoseriCompanyName = data?.karoseriCompany?.name;
	// assetConditionId
	if (data?.assetCondition?.id)
		result.assetConditionId = Number(data?.assetCondition?.id);
	// assetUsageId
	if (data?.assetUsage?.id) result.assetUsageId = Number(data?.assetUsage?.id);
	// assetCategoryId
	if (data?.assetCategory) result.assetCategory = data?.assetCategory;
	// assetBrandId
	if (data?.assetBrand?.id) result.assetBrandId = data?.assetBrand?.id;
	// assetType
	if (data?.assetType) result.assetType = data?.assetType;
	// assetTypeId
	if (data?.assetTypeId) result.assetTypeId = data?.assetTypeId;
	// assetId
	if (data?.asset?.id) result.assetId = data?.asset?.id;
	// assetManufactureYear
	if (data?.assetManufactureYear)
		result.assetManufactureYear = data?.assetManufactureYear;
	// otrTypeId
	if (data?.otrType?.id) result.otrTypeId = Number(data?.otrType?.id);
	if (data?.otrOtherHeavyEquipmentComponentAmount)
		result.otrOtherHeavyEquipmentComponentAmount = Number(
			data?.otrOtherHeavyEquipmentComponentAmount,
		);
	if (data?.otrHeavyEquipmentAmount)
		result.otrHeavyEquipmentAmount = Number(data?.otrHeavyEquipmentAmount);
	if (
		data?.kjppLiquidityAmount !== undefined &&
		data?.kjppLiquidityAmount !== null
	) {
		result.kjppLiquidityAmount = Number(data.kjppLiquidityAmount);
	} else {
		result.kjppLiquidityAmount = 0;
	}
	// isHeavyEquipmentAndOtherComponent
	if (
		data?.isHeavyEquipmentAndOtherComponent !== undefined &&
		data?.isHeavyEquipmentAndOtherComponent !== null
	)
		result.isHeavyEquipmentAndOtherComponent =
			data?.isHeavyEquipmentAndOtherComponent;
	// isChasisAndKaroseri
	if (
		data?.isChasisAndKaroseri !== undefined &&
		data?.isChasisAndKaroseri !== null
	)
		result.isChasisAndKaroseri = data?.isChasisAndKaroseri;
	// otrChasisAmount
	if (data?.otrChasisAmount !== undefined && data?.otrChasisAmount !== null)
		result.otrChasisAmount = Number(data?.otrChasisAmount);
	// otrKaroseriAmount
	if (data?.otrKaroseriAmount !== undefined && data?.otrKaroseriAmount !== null)
		result.otrKaroseriAmount = Number(data?.otrKaroseriAmount);

	// financingProductId
	if (data?.financingProduct?.id)
		result.financingProductId = Number(data?.financingProduct?.id);
	// productId
	if (data?.product?.id) result.productId = String(data?.product?.id);
	// productOffering
	if (data?.productOffering) result.productOffering = data?.productOffering;
	// installmentTypeId
	if (data?.installmentType?.id)
		result.installmentTypeId = Number(data?.installmentType?.id);
	// karoseriCalculation
	if (data?.karoseriCalculation) {
		result.karoseriCalculation = {
			type: data?.karoseriCalculation?.type ?? '',
			bpkbOwnerName: data?.karoseriCalculation?.bpkbOwnerName ?? '',
			unitPrice: data?.karoseriCalculation?.unitPrice
				? String(data?.karoseriCalculation?.unitPrice)
				: '',
			downPaymentPercentage: data?.karoseriCalculation?.downPaymentPercentage
				? String(data?.karoseriCalculation?.downPaymentPercentage)
				: '',
		};
	}
	// chasisCalculation
	if (data?.chasisCalculation) {
		result.chasisCalculation = {
			type: data?.chasisCalculation?.type ?? '',
			bpkbOwnerName: data?.chasisCalculation?.bpkbOwnerName ?? '',
			unitPrice: data?.chasisCalculation?.unitPrice
				? String(data?.chasisCalculation?.unitPrice)
				: '',
			downPaymentPercentage: data?.chasisCalculation?.downPaymentPercentage
				? String(data?.chasisCalculation?.downPaymentPercentage)
				: '',
		};
	}
	// requestedOtr
	if (data?.requestedOtrAmount)
		result.requestedOtrAmount = data?.requestedOtrAmount;
	// phJmbAmount
	if (data?.phJmbAmount !== undefined && data?.phJmbAmount !== null)
		result.phJmbAmount = Number(data?.phJmbAmount);
	if (
		data?.isHeavyEquipmentAndOtherComponent !== undefined &&
		data?.isHeavyEquipmentAndOtherComponent !== null
	)
		result.isHeavyEquipmentAndOtherComponent =
			data?.isHeavyEquipmentAndOtherComponent;
	// otrSubsidyAmount
	if (data?.otrSubsidyAmount !== undefined && data?.otrSubsidyAmount !== null) {
		result.otrSubsidyAmount = Number(data.otrSubsidyAmount);
	} else {
		result.otrSubsidyAmount = 0;
	}
	// downPaymentPercent
	if (data?.downPaymentPercentage)
		result.downPaymentPercentage = data?.downPaymentPercentage;
	// effectiveRatePercent
	if (data?.effectiveRatePercentage)
		result.effectiveRatePercentage = data?.effectiveRatePercentage;
	// tenorInYear
	if (data?.tenorInYear?.id) result.tenorInYear = Number(data?.tenorInYear?.id);
	// paymentBreakdownPercentages
	if (data?.paymentBreakdownPercentages?.length)
		result.paymentBreakdownPercentages = data?.paymentBreakdownPercentages;
	// totalUnit
	if (data?.totalUnit) result.totalUnit = data?.totalUnit;
	// provisionPayment
	if (data?.provisionPayment) result.provisionPayment = data?.provisionPayment;
	// provisionPercentage
	if (data?.provisionFeePercentage)
		result.provisionPercentage = data?.provisionFeePercentage;
	// servicingFeePercent
	if (data?.servicingFee) result.servicingFee = data?.servicingFee;
	// administrationPayment
	if (data?.administrationPayment)
		result.administrationPayment = data?.administrationPayment;
	// administrationFee
	if (data?.administrationFee)
		result.administrationFee = data?.administrationFee;
	// fiduciaryPayment
	if (data?.fiduciaryPayment) result.fiduciaryPayment = data?.fiduciaryPayment;
	// fiduciaryFee
	if (data?.fiduciaryFee !== undefined && data?.fiduciaryFee !== null)
		result.fiduciaryFee = data?.fiduciaryFee;
	if (data?.isBundlingFiduciaryFee !== undefined)
		result.isBundlingFiduciaryFee = data?.isBundlingFiduciaryFee;
	// totalDownPaymentSubsidyAmount
	if (
		data?.totalDownPaymentSubsidyAmount !== undefined &&
		data?.totalDownPaymentSubsidyAmount !== null
	) {
		result.totalDownPaymentSubsidyAmount = Number(
			data.totalDownPaymentSubsidyAmount,
		);
	} else {
		result.totalDownPaymentSubsidyAmount = 0;
	}
	// mtfClauseStartDate
	if (data?.mtfClauseStartDate && data.mtfClauseStartDate.trim() !== '') {
		const mtfStartDate = dayjs(data.mtfClauseStartDate);
		if (mtfStartDate.isValid()) {
			result.mtfClauseStartDate = mtfStartDate;
		}
	}
	// mtfClauseEndDate
	if (data?.mtfClauseEndDate && data.mtfClauseEndDate.trim() !== '') {
		const mtfEndDate = dayjs(data.mtfClauseEndDate);
		if (mtfEndDate.isValid()) {
			result.mtfClauseEndDate = mtfEndDate;
		}
	}
	// bankerClauseStartDate
	if (data?.bankerClauseStartDate && data.bankerClauseStartDate.trim() !== '') {
		const bankerStartDate = dayjs(data.bankerClauseStartDate);
		if (bankerStartDate.isValid()) {
			result.bankerClauseStartDate = bankerStartDate;
		}
	}
	// bankerClauseEndDate
	if (data?.bankerClauseEndDate && data.bankerClauseEndDate.trim() !== '') {
		const bankerEndDate = dayjs(data.bankerClauseEndDate);
		if (bankerEndDate.isValid()) {
			result.bankerClauseEndDate = bankerEndDate;
		}
	}
	// insurancePaymentId
	if (data?.insurancePayment?.id)
		result.insurancePaymentId = Number(data?.insurancePayment?.id);
	// insuranceRegion
	if (data?.insuranceRegion?.id)
		result.insuranceRegion = Number(data?.insuranceRegion?.id);
	// insuranceRateLimitId
	if (data?.insuranceRateLimit?.id)
		result.insuranceRateLimitId = Number(data?.insuranceRateLimit?.id);
	// insuranceTenorId
	if (
		data?.insuranceTenorType?.id !== undefined &&
		data?.insuranceTenorType?.id !== null
	)
		result.insuranceTenorTypeId = Number(data?.insuranceTenorType?.id);

	// New Insurance Fields
	if (data?.insuranceDiscountPercentage)
		result.insuranceDiscountPercentage = data.insuranceDiscountPercentage;
	if (data?.insurancePaymentFullCashTypeId)
		result.insurancePaymentFullCashTypeId = data.insurancePaymentFullCashTypeId;
	if (data?.insurancePaymentSchemeGiroTypeId)
		result.insurancePaymentSchemeGiroTypeId =
			data.insurancePaymentSchemeGiroTypeId;
	if (data?.insuranceGiroTenor)
		result.insuranceGiroTenor = data.insuranceGiroTenor;
	// isFlood
	if (data?.isFlood !== undefined && data?.isFlood !== null)
		result.isFlood = data?.isFlood;
	// isEarthquake
	if (data?.isEarthquake !== undefined && data?.isEarthquake !== null)
		result.isEarthquake = data?.isEarthquake;
	// isRiot
	if (data?.isRiot !== undefined && data?.isRiot !== null)
		result.isRiot = data?.isRiot;
	// isTerrorism
	if (data?.isTerrorism !== undefined && data?.isTerrorism !== null)
		result.isTerrorism = data?.isTerrorism;
	// tjhAmount
	if (data?.tjhAmount?.id !== undefined && data?.tjhAmount?.id !== null) {
		const tjhId = Number(data.tjhAmount.id);
		if (!isNaN(tjhId) && tjhId > 0) {
			result.tjhAmount = tjhId;
		} else {
			// Use 0 for invalid/zero values (API expects number)
			result.tjhAmount = 0;
		}
	} else {
		// Use 0 for null/undefined values (API expects number)
		result.tjhAmount = 0;
	}
	// driverPersonalAccident
	if (
		data?.driverPersonalAccident?.id !== undefined &&
		data?.driverPersonalAccident?.id !== null
	) {
		const driverPaId = Number(data.driverPersonalAccident.id);
		if (!isNaN(driverPaId) && driverPaId > 0) {
			result.driverPersonalAccident = driverPaId;
		} else {
			// Use 0 for invalid/zero values (API expects number)
			result.driverPersonalAccident = 0;
		}
	} else {
		// Use 0 for null/undefined values (API expects number)
		result.driverPersonalAccident = 0;
	}
	// passengerPersonalAccident
	if (
		data?.passengerPersonalAccident?.id !== undefined &&
		data?.passengerPersonalAccident?.id !== null
	) {
		const passengerPaId = Number(data.passengerPersonalAccident.id);
		if (!isNaN(passengerPaId) && passengerPaId > 0) {
			result.passengerPersonalAccident = passengerPaId;
		} else {
			// Use 0 for invalid/zero values (API expects number)
			result.passengerPersonalAccident = 0;
		}
	} else {
		// Use 0 for null/undefined values (API expects number)
		result.passengerPersonalAccident = 0;
	}

	// totalPassenger
	if (data?.totalPassenger !== undefined && data?.totalPassenger !== null)
		result.totalPassenger = data?.totalPassenger;
	// isHic
	if (data?.isHic !== undefined && data?.isHic !== null)
		result.isHic = data?.isHic;
	// isGap
	if (data?.isGap !== undefined && data?.isGap !== null)
		result.isGap = data?.isGap;

	// insurancePolicyDocument
	if (data?.insurancePolicyDocument) {
		result.insurancePolicyDocument = data.insurancePolicyDocument;
	}
	// insurancePaymentDocument
	if (data?.insurancePaymentDocument) {
		result.insurancePaymentDocument = data.insurancePaymentDocument;
	}

	return result;
};
